import { useState, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ApiError, userAuth } from '../api.js';

export function ResetPasswordPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await userAuth.resetPassword(token, password);
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'BAD_TOKEN') {
        setError('That link is invalid or expired. Request a new one.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Network error.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="max-w-sm mx-auto py-16 text-center">
        <h1 className="font-serif text-3xl mb-2">No reset token</h1>
        <p className="text-muted text-sm mb-6">This page needs a token from a reset email.</p>
        <Link to="/forgot-password" className="text-accent underline-offset-2 hover:underline text-sm">Request a reset link</Link>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto py-16">
      <h1 className="font-serif text-3xl mb-2">Set a new password</h1>
      <form onSubmit={handle} className="space-y-4 mt-6">
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="pw">New password</label>
          <input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="new-password"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 font-mono text-sm"
            required
            minLength={8}
          />
        </div>
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="confirm">Confirm</label>
          <input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 font-mono text-sm"
            required
            minLength={8}
          />
        </div>
        {error && (
          <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">{error}</div>
        )}
        <button
          type="submit"
          disabled={submitting || password.length < 8 || !confirm}
          className="w-full bg-ink text-parchment py-2 rounded-md hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}
